import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { auth } from '../../../../config/firebase';
import { useAuthStore } from '../../../../store/auth';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { needsPublicBookingSessionPicker, isGroupDropInPublicBooking } from '../publicBookingSessionMode';
import { resolveGroupDropInSessionDate } from '../resolveGroupDropInSessionDate';
import { AuthService } from '../../../../services/auth';
import { buildEmailVerificationPageUrl } from '../../../../utils/emailVerificationRedirect';
import { PublicBookingGuestEOIForm } from '../PublicBookingGuestEOIForm';
import { AcademyGuestRegistrationBlock } from '../AcademyGuestRegistrationBlock';
import { useOpenAcademyBookingLogin } from '@/hooks/useOpenAcademyBookingLogin';
import {
  appendPublicBookingWizardContextQueries,
  publicBookingBasePath,
  publicBookingStepPath,
  resolveManualInviteTokenForNavigation,
} from '../bookingPaths';
import { publicBookingService } from '../../../../services/publicBooking';
import { userFormSchema } from '../schemas';
import type { UserFormData } from '../schemas';

export function BookingAuthStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const openBookingLogin = useOpenAcademyBookingLogin();
  const [searchParams] = useSearchParams();
  const { user: authStoreUser, setUser: setAuthStoreUser, isInitialized } = useAuthStore();
  const lesson = usePublicBookingStore((s) => s.lesson);
  const term = usePublicBookingStore((s) => s.term);
  const academy = usePublicBookingStore((s) => s.academy);
  const booking = usePublicBookingStore((s) => s.booking);
  const selectedSessionDates = usePublicBookingStore((s) => s.selectedSessionDates);
  const setSelectedSessionDates = usePublicBookingStore((s) => s.setSelectedSessionDates);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const lessonOverrides = usePublicBookingStore((s) => s.lessonOverrides);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const waitlistClaimEntryId = usePublicBookingStore((s) => s.waitlistClaimEntryId);

  const firebaseUserEarly = auth.currentUser;
  const isRealFirebaseUser = !!(firebaseUserEarly && !firebaseUserEarly.isAnonymous);

  const showGuestEoiFirst =
    !!lesson?.isInvitationOnly &&
    !invitationToken &&
    !waitlistClaimEntryId &&
    !!academy?.id &&
    !!academySlug &&
    !!lessonId;

  const showGuestEoiWizard = showGuestEoiFirst && !isRealFirebaseUser;

  const setUserType = usePublicBookingStore((s) => s.setUserType);
  const setUserFormData = usePublicBookingStore((s) => s.setUserFormData);

  const [registerSubmitting, setRegisterSubmitting] = useState(false);

  const registerForm = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  const { setValue: setRegisterEmailField, getValues: getRegisterEmailFields } = registerForm;

  useEffect(() => {
    const token = resolveManualInviteTokenForNavigation(searchParams, manualInviteToken);
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    void publicBookingService.getManualInvitePreview(token).then((res) => {
      if (cancelled || !res.success || res.data?.inviteEmail == null || res.data.inviteEmail === '') {
        return;
      }
      const cur = getRegisterEmailFields('email');
      if (cur && cur.trim() !== '') return;
      setRegisterEmailField('email', res.data.inviteEmail);
    });
    return () => {
      cancelled = true;
    };
  }, [searchParams, manualInviteToken, setRegisterEmailField, getRegisterEmailFields]);

  const needsCampSessionPicker = needsPublicBookingSessionPicker(
    lesson,
    campBookingMode,
    groupBookingMode
  );
  const isGroupDropIn = isGroupDropInPublicBooking(lesson, groupBookingMode);

  const dropInNextSessionDate = useCallback((): string | null => {
    if (!isGroupDropIn || !lesson) return null;
    return (
      selectedSessionDates[0] ??
      resolveGroupDropInSessionDate(lesson, term, [], lessonOverrides)
    );
  }, [isGroupDropIn, lesson, term, selectedSessionDates, lessonOverrides]);

  const isDropInBlocked = isGroupDropIn && !dropInNextSessionDate();

  const resolvePostAuthSegment = useCallback((): 'sessions' | 'details' | null => {
    if (needsCampSessionPicker && selectedSessionDates.length === 0) {
      return 'sessions';
    }
    if (isGroupDropIn && lesson) {
      const next = dropInNextSessionDate();
      if (next) {
        if (selectedSessionDates[0] !== next) {
          setSelectedSessionDates([next]);
        }
        return 'details';
      }
      toast.error('No upcoming sessions are available for drop-in booking.');
      return null;
    }
    return 'details';
  }, [
    needsCampSessionPicker,
    isGroupDropIn,
    lesson,
    selectedSessionDates,
    setSelectedSessionDates,
    dropInNextSessionDate,
  ]);

  // Logged-in user (non-anonymous) returning to booking: skip auth, go to sessions or details
  useEffect(() => {
    if (!isInitialized) return;
    if (!academySlug || !lessonId || !lesson || !academy) return;
    if (booking) return;

    const firebaseUser = auth.currentUser;
    const isAnonymous = firebaseUser?.isAnonymous === true;
    const isLoggedIn = !!(firebaseUser && !isAnonymous && (authStoreUser || firebaseUser.uid));

    if (isLoggedIn) {
      if (!authStoreUser && firebaseUser) {
        void AuthService.refreshUserProfile()
          .then((user) => setAuthStoreUser(user))
          .catch(() => {});
      }
      setUserType('returning');
      const segment = resolvePostAuthSegment();
      if (!segment) return;
      navigate(
        appendPublicBookingWizardContextQueries(
          publicBookingStepPath(academySlug, lessonId, segment),
          { invitationToken, manualInviteToken }
        ),
        { replace: true }
      );
    }
  }, [
    isInitialized,
    academySlug,
    lessonId,
    authStoreUser,
    lesson,
    term,
    academy,
    booking,
    resolvePostAuthSegment,
    navigate,
    setUserType,
    setAuthStoreUser,
    invitationToken,
    manualInviteToken,
  ]);

  const goAfterAuth = useCallback(() => {
    if (!academySlug || !lessonId) return;
    const segment = resolvePostAuthSegment();
    if (!segment) return;
    navigate(
      appendPublicBookingWizardContextQueries(publicBookingStepPath(academySlug, lessonId, segment), {
        invitationToken,
        manualInviteToken,
      })
    );
  }, [
    academySlug,
    lessonId,
    resolvePostAuthSegment,
    navigate,
    invitationToken,
    manualInviteToken,
  ]);

  const handleEmailSubmit = useCallback(
    async (data: UserFormData) => {
      if (!academySlug || !lessonId) return;
      try {
        setRegisterSubmitting(true);
        setUserFormData(data);
        setUserType('new');

        const returnUrl = appendPublicBookingWizardContextQueries(
          publicBookingBasePath(academySlug, lessonId),
          { invitationToken, manualInviteToken }
        );
        const { user, verificationEmailFailed } = await AuthService.signUpAsGuardian(
          data.email,
          data.password,
          data.firstName,
          data.lastName,
          returnUrl,
          {
            termsAccepted: data.termsAccepted,
            privacyAccepted: data.privacyAccepted,
            termsVersion: LEGAL_VERSIONS.TERMS,
            privacyVersion: LEGAL_VERSIONS.PRIVACY,
            marketingEmailConsent: data.marketingEmailConsent || false,
          },
          academy?.id
        );

        setAuthStoreUser(user);

        if (verificationEmailFailed) {
          navigate(buildEmailVerificationPageUrl({ email: data.email, returnUrl, academySlug }), {
            state: { verificationEmailSendFailed: true },
          });
          return;
        }

        toast.success('Account created! Please fill in the student details to continue.');
        goAfterAuth();
      } catch (authError: unknown) {
        const err = authError as { code?: string };
        if (err.code === 'auth/email-already-in-use') {
          toast.error('An account with this email already exists. Please sign in instead.');
          openBookingLogin(
            academySlug,
            lessonId,
            {
              invitationToken,
              manualInviteToken,
              waitlistEntryId: waitlistClaimEntryId,
            },
            {
              prefillEmail: data.email,
              message: 'An account with this email already exists. Please sign in to continue your booking.',
            }
          );
          return;
        }
        throw authError;
      } finally {
        setRegisterSubmitting(false);
      }
    },
    [
      academy?.id,
      academySlug,
      invitationToken,
      lessonId,
      manualInviteToken,
      navigate,
      waitlistClaimEntryId,
      setAuthStoreUser,
      setUserFormData,
      setUserType,
      goAfterAuth,
    ]
  );

  if (!isInitialized) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">Checking your sign-in status…</div>
    );
  }

  if (showGuestEoiWizard) {
    return (
      <PublicBookingGuestEOIForm
        academySlug={academySlug!}
        lessonId={lessonId!}
        academyId={academy!.id}
        lessonName={lesson?.name}
        invitationToken={invitationToken}
        manualInviteToken={manualInviteToken}
      />
    );
  }

  if (isDropInBlocked) {
    return (
      <div className="py-8 text-center space-y-3">
        <p className="text-sm text-muted-foreground">
          No upcoming sessions are available for drop-in booking on this lesson.
        </p>
        <p className="text-xs text-muted-foreground">
          Cancelled or postponed dates are excluded. Try again later or choose full-term booking if available.
        </p>
      </div>
    );
  }

  return (
    <AcademyGuestRegistrationBlock
      registerSubmitting={registerSubmitting}
      form={registerForm}
      onEmailSubmit={handleEmailSubmit}
      submitButtonLabel="Create account and continue"
      defaultCountrySource={{
        country: academy?.country,
        region: academy?.region,
      }}
    />
  );
}
